import React, { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { toast } from "@/hooks/use-toast";

import { Database } from "@/integrations/supabase/types";

type EstadoObra = Database["public"]["Enums"]["estado_obra"];
type TipoTrabajo = Database["public"]["Enums"]["tipo_trabajo"];

export interface Obra {
  id: string;
  numero_obra?: string | null;
  numero_presupuesto?: string | null;
  nombre: string;
  descripcion?: string | null;
  direccion?: string | null;
  cliente_id: string;
  horas_estimadas: number;
  horas_consumidas: number;
  coste_consumido: number;
  coste_mano_obra_estimado: number;
  precio_venta?: number | null;
  estado: EstadoObra;
  fecha_inicio: string;
  fecha_fin_estimada?: string | null;
  fecha_fin_real?: string | null;
  created_at: string;
  updated_at: string;
  // Join fields
  cliente?: {
    nombre: string;
  } | null;
}

export interface ObraJornada {
  id: string;
  obra_id?: string | null;
  orden_trabajo_id?: string | null;
  cliente_id?: string | null;
  tecnico_id: string;
  fecha: string;
  hora_inicio: string;
  hora_fin?: string | null;
  horas_trabajadas?: number | null;
  coste_hora_aplicado?: number | null;
  coste_total?: number | null;
  uso_furgoneta?: boolean;
  importe_furgoneta_aplicado?: number | null;
  descripcion_trabajo?: string | null;
  tipo_trabajo?: TipoTrabajo | null;
  es_hora_extra: boolean;
  estado_validacion?: 'pendiente' | 'aprobada' | 'rechazada';
  motivo_rechazo?: string | null;
  revisada_por?: string | null;
  fecha_revision?: string | null;
  created_at: string;
  updated_at: string;
  // Join fields
  obra?: {
    nombre: string;
  } | null;
  orden_trabajo?: {
    numero_ot: string;
    titulo: string;
  } | null;
  cliente?: {
    nombre: string;
  } | null;
  tecnico?: {
    nombre: string;
  } | null;
}

// Type for origin when creating a jornada
export type JornadaOrigen = 'obra' | 'orden_trabajo' | 'cliente';

export interface ObrasContextType {
  obras: Obra[];
  jornadas: ObraJornada[];
  loading: boolean;
  createObra: (obra: Omit<Obra, 'id' | 'created_at' | 'updated_at' | 'horas_consumidas'>) => Promise<void>;
  updateObra: (id: string, obra: Partial<Obra>) => Promise<void>;
  deleteObra: (id: string) => Promise<void>;
  createJornada: (jornada: {
    obra_id?: string | null;
    orden_trabajo_id?: string | null;
    cliente_id?: string | null;
    tecnico_id: string;
    fecha: string;
    hora_inicio: string;
    hora_fin?: string | null;
    descripcion_trabajo?: string | null;
    tipo_trabajo: TipoTrabajo;
    es_hora_extra: boolean;
  }) => Promise<ObraJornada>;
  updateJornada: (id: string, jornada: Partial<ObraJornada>) => Promise<void>;
  deleteJornada: (id: string) => Promise<void>;
  getObraById: (id: string) => Obra | undefined;
  getJornadasByObra: (obraId: string) => ObraJornada[];
  getJornadasByTecnico: (tecnicoId: string) => ObraJornada[];
  getJornadasByOrdenTrabajo: (ordenTrabajoId: string) => ObraJornada[];
  refreshObras: () => Promise<void>;
  refreshJornadas: () => Promise<void>;
}

const ObrasContext = createContext<ObrasContextType | undefined>(undefined);

export const useObrasContext = () => {
  const context = useContext(ObrasContext);
  if (!context) {
    throw new Error("useObrasContext must be used within an ObrasProvider");
  }
  return context;
};

export const ObrasProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [obras, setObras] = useState<Obra[]>([]);
  const [jornadas, setJornadas] = useState<ObraJornada[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, perfil } = useAuth();

  const loadObras = async () => {
    try {
      console.log('Loading obras...');
      const { data, error } = await supabase
        .from('obras')
        .select(`
          *,
          cliente:clientes(nombre)
        `)
        .order('created_at', { ascending: false });

      console.log('Obras query result:', { data, error });
      
      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      if (data) {
        console.log('Raw obras data:', data);
        const mappedObras: Obra[] = data.map((item: any) => ({
          id: item.id,
          numero_obra: (item as any).numero_obra ?? null,
          nombre: item.nombre,
          descripcion: item.descripcion,
          direccion: item.direccion,
          cliente_id: item.cliente_id,
          horas_estimadas: Number(item.horas_estimadas) || 0,
          horas_consumidas: Number(item.horas_consumidas) || 0,
          coste_consumido: Number((item as any).coste_consumido) || 0,
          coste_mano_obra_estimado: Number((item as any).coste_mano_obra_estimado) || 0,
          precio_venta: (item as any).precio_venta != null ? Number((item as any).precio_venta) : null,
          estado: item.estado,
          fecha_inicio: item.fecha_inicio,
          fecha_fin_estimada: item.fecha_fin_estimada,
          fecha_fin_real: item.fecha_fin_real,
          created_at: item.created_at,
          updated_at: item.updated_at,
          cliente: item.cliente && typeof item.cliente === 'object' && !Array.isArray(item.cliente)
            ? { nombre: item.cliente.nombre }
            : null
        }));
        
        console.log('Mapped obras:', mappedObras);
        setObras(mappedObras);
      } else {
        console.log('No obras data received');
        setObras([]);
      }
    } catch (error) {
      console.error('Error loading obras:', error);
      toast({
        title: "Error",
        description: `Error al cargar las obras: ${error.message || 'Error desconocido'}`,
        variant: "destructive",
      });
    }
  };

  const loadJornadas = async () => {
    try {
      console.log('Loading jornadas...');
      const { data, error } = await supabase
        .from('obra_jornadas')
        .select(`
          *,
          obra:obras(nombre),
          orden_trabajo:ordenes_trabajo(numero_ot, titulo),
          cliente:clientes(nombre),
          tecnico:perfiles(nombre, user_id)
        `)
        .order('fecha', { ascending: false });

      console.log('Jornadas query result:', { data, error });

      if (error) {
        console.error('Supabase error loading jornadas:', error);
        throw error;
      }

      if (data) {
        console.log('Raw jornadas data:', data);
        const mappedJornadas: ObraJornada[] = data.map((item: any) => ({
          id: item.id,
          obra_id: item.obra_id,
          orden_trabajo_id: item.orden_trabajo_id,
          cliente_id: item.cliente_id,
          tecnico_id: item.tecnico_id,
          fecha: item.fecha,
          hora_inicio: item.hora_inicio,
          hora_fin: item.hora_fin,
          horas_trabajadas: Number(item.horas_trabajadas) || null,
          coste_hora_aplicado: item.coste_hora_aplicado != null ? Number(item.coste_hora_aplicado) : null,
          coste_total: item.coste_total != null ? Number(item.coste_total) : null,
          uso_furgoneta: Boolean(item.uso_furgoneta),
          importe_furgoneta_aplicado: item.importe_furgoneta_aplicado != null ? Number(item.importe_furgoneta_aplicado) : null,
          descripcion_trabajo: item.descripcion_trabajo,
          tipo_trabajo: item.tipo_trabajo,
          es_hora_extra: Boolean(item.es_hora_extra),
          estado_validacion: item.estado_validacion ?? 'pendiente',
          motivo_rechazo: item.motivo_rechazo ?? null,
          revisada_por: item.revisada_por ?? null,
          fecha_revision: item.fecha_revision ?? null,
          created_at: item.created_at,
          updated_at: item.updated_at,
          obra: item.obra && typeof item.obra === 'object' && !Array.isArray(item.obra)
            ? { nombre: item.obra.nombre }
            : null,
          orden_trabajo: item.orden_trabajo && typeof item.orden_trabajo === 'object' && !Array.isArray(item.orden_trabajo)
            ? { numero_ot: item.orden_trabajo.numero_ot, titulo: item.orden_trabajo.titulo }
            : null,
          cliente: item.cliente && typeof item.cliente === 'object' && !Array.isArray(item.cliente)
            ? { nombre: item.cliente.nombre }
            : null,
          tecnico: item.tecnico && typeof item.tecnico === 'object' && !Array.isArray(item.tecnico)
            ? { nombre: item.tecnico.nombre }
            : null
        }));
        
        console.log('Mapped jornadas:', mappedJornadas);
        setJornadas(mappedJornadas);
      } else {
        console.log('No jornadas data received');
        setJornadas([]);
      }
    } catch (error) {
      console.error('Error loading jornadas:', error);
      toast({
        title: "Error",
        description: `Error al cargar las jornadas: ${error.message || 'Error desconocido'}`,
        variant: "destructive",
      });
    }
  };

  const createObra = async (obra: Omit<Obra, 'id' | 'created_at' | 'updated_at' | 'horas_consumidas'>) => {
    try {
      const obraConEmpresa = {
        ...obra,
        empresa_id: perfil?.empresa_id,
      };
      const { data, error } = await supabase
        .from('obras')
        .insert([obraConEmpresa as any])
        .select()
        .single();

      if (error) throw error;

      await loadObras();
      toast({
        title: "Éxito",
        description: "Obra creada correctamente",
      });
    } catch (error) {
      console.error('Error creating obra:', error);
      toast({
        title: "Error",
        description: "Error al crear la obra",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateObra = async (id: string, obra: Partial<Obra>) => {
    try {
      const { error } = await supabase
        .from('obras')
        .update(obra as any)
        .eq('id', id);

      if (error) throw error;

      await loadObras();
      toast({
        title: "Éxito",
        description: "Obra actualizada correctamente",
      });
    } catch (error) {
      console.error('Error updating obra:', error);
      toast({
        title: "Error",
        description: "Error al actualizar la obra",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteObra = async (id: string) => {
    try {
      const { error } = await supabase
        .from('obras')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadObras();
      toast({
        title: "Éxito",
        description: "Obra eliminada correctamente",
      });
    } catch (error) {
      console.error('Error deleting obra:', error);
      toast({
        title: "Error",
        description: "Error al eliminar la obra",
        variant: "destructive",
      });
      throw error;
    }
  };

  const createJornada = async (jornada: {
    obra_id?: string | null;
    orden_trabajo_id?: string | null;
    cliente_id?: string | null;
    tecnico_id: string;
    fecha: string;
    hora_inicio: string;
    hora_fin?: string | null;
    descripcion_trabajo?: string | null;
    tipo_trabajo: TipoTrabajo;
    es_hora_extra: boolean;
  }) => {
    try {
      const { data, error } = await supabase
        .from('obra_jornadas')
        .insert([jornada])
        .select()
        .single();

      if (error) throw error;

      await Promise.all([loadJornadas(), loadObras()]);
      toast({
        title: "Éxito",
        description: "Jornada registrada correctamente",
      });
      return data as ObraJornada;
    } catch (error) {
      console.error('Error creating jornada:', error);
      toast({
        title: "Error",
        description: "Error al registrar la jornada",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateJornada = async (id: string, jornada: Partial<ObraJornada>) => {
    try {
      const { error } = await supabase
        .from('obra_jornadas')
        .update(jornada as any)
        .eq('id', id);

      if (error) throw error;

      await Promise.all([loadJornadas(), loadObras()]);
      toast({
        title: "Éxito",
        description: "Jornada actualizada correctamente",
      });
    } catch (error) {
      console.error('Error updating jornada:', error);
      toast({
        title: "Error",
        description: "Error al actualizar la jornada",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteJornada = async (id: string) => {
    try {
      const { error } = await supabase
        .from('obra_jornadas')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await Promise.all([loadJornadas(), loadObras()]);
      toast({
        title: "Éxito",
        description: "Jornada eliminada correctamente",
      });
    } catch (error) {
      console.error('Error deleting jornada:', error);
      toast({
        title: "Error",
        description: "Error al eliminar la jornada",
        variant: "destructive",
      });
      throw error;
    }
  };

  const getObraById = (id: string) => obras.find(obra => obra.id === id);
  const getJornadasByObra = (obraId: string) => jornadas.filter(jornada => jornada.obra_id === obraId);
  const getJornadasByTecnico = (tecnicoId: string) => jornadas.filter(jornada => jornada.tecnico_id === tecnicoId);
  const getJornadasByOrdenTrabajo = (ordenTrabajoId: string) => jornadas.filter(jornada => jornada.orden_trabajo_id === ordenTrabajoId);

  const refreshObras = () => loadObras();
  const refreshJornadas = () => loadJornadas();

  useEffect(() => {
    console.log('ObrasContext useEffect triggered, user:', user);
    if (user) {
      console.log('User found, loading obras and jornadas...');
      setLoading(true);
      Promise.all([loadObras(), loadJornadas()])
        .finally(() => {
          console.log('Loading completed');
          setLoading(false);
        });
    } else {
      console.log('No user found, setting empty state');
      setObras([]);
      setJornadas([]);
      setLoading(false);
    }
  }, [user]);

  const value: ObrasContextType = {
    obras,
    jornadas,
    loading,
    createObra,
    updateObra,
    deleteObra,
    createJornada,
    updateJornada,
    deleteJornada,
    getObraById,
    getJornadasByObra,
    getJornadasByTecnico,
    getJornadasByOrdenTrabajo,
    refreshObras,
    refreshJornadas,
  };

  return (
    <ObrasContext.Provider value={value}>
      {children}
    </ObrasContext.Provider>
  );
};